
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Coins, MapPin, Phone, Truck, Star, ChefHat } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const AchatInox = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Achat Inox - Rachat Éviers, Cuves, Équipements Industriels | Groupe Fabre"
        description="Rachat d'inox au meilleur prix : éviers, cuves, équipements industriels, tôles inox. Pesée certifiée et paiement immédiat."
        canonical="/nos-services/achat-metaux/inox"
        keywords="achat inox, rachat éviers inox, cuves inox, équipements industriels, prix inox, inoxydable"
      />
      <Navbar />
      
      <main className="flex-1 mt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-gray-400 to-gray-500 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center mb-4">
                <ChefHat className="h-12 w-12 mr-4" />
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold">Achat d'Inox</h1>
                  <p className="text-xl mt-2 opacity-90">Rachat d'éviers, cuves et équipements industriels</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-fabre-gray mb-6">
                  Rachat d'inox - Acier inoxydable
                </h2>
                <p className="text-gray-600 mb-6">
                  L'<strong>inox</strong> (acier inoxydable) est un matériau noble très recherché. 
                  <strong>Groupe Fabre Environnement</strong> rachète tous types d'inox : 
                  éviers, cuves industrielles, équipements alimentaires et tôlerie inox.
                </p>
                
                <h3 className="text-xl font-bold text-fabre-gray mb-4">Types d'inox rachetés</h3>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-500 text-white mr-3">•</span>
                    <span><strong>Éviers :</strong> cuisine, laboratoire, collectivités</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-500 text-white mr-3">•</span>
                    <span><strong>Cuves :</strong> stockage, process, fermentation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-500 text-white mr-3">•</span>
                    <span><strong>Équipements :</strong> alimentaires, pharmaceutiques</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-500 text-white mr-3">•</span>
                    <span><strong>Tôlerie :</strong> plaques, tubes, profilés</span>
                  </li>
                </ul>

                <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
                  <h4 className="font-bold text-blue-800 mb-3">Avantages de l'inox</h4>
                  <ul className="text-blue-700 text-sm space-y-2">
                    <li>• Résistance à la corrosion exceptionnelle</li>
                    <li>• Recyclable à 100% sans perte de propriétés</li>
                    <li>• Hygiène alimentaire garantie</li>
                    <li>• Durabilité et résistance mécanique</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-fabre-gray mb-6">Expertise inox</h3>
                <p className="text-gray-600 mb-6">
                  Nous identifions les différentes nuances d'inox (304, 316, 430...) 
                  pour vous proposer la meilleure valorisation selon la qualité.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center">
                    <Coins className="h-5 w-5 text-gray-600 mr-3" />
                    <span>Prix selon nuance inox</span>
                  </div>
                  <div className="flex items-center">
                    <ChefHat className="h-5 w-5 text-gray-600 mr-3" />
                    <span>Spécialiste équipements</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-gray-600 mr-3" />
                    <span>Test magnétique inclus</span>
                  </div>
                </div>

                <Link to="/contact" className="block">
                  <button className="w-full px-6 py-3 bg-gray-600 text-white rounded-md font-medium hover:bg-gray-700 transition-colors">
                    Demander prix inox
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Nuances d'inox */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-fabre-gray mb-8 text-center">
              Principales nuances d'inox et applications
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Inox 304 (18/10)</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Usage général, alimentaire, éviers domestiques
                </p>
                <div className="text-green-600 font-medium">Prix standard</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Inox 316 (18/12)</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Résistance marine, chimie, pharmaceutique
                </p>
                <div className="text-blue-600 font-medium">Prix premium</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Inox 430</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Magnétique, décoratif, électroménager
                </p>
                <div className="text-orange-600 font-medium">Prix ajusté</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AchatInox;
